import React, { useMemo } from 'react';
import './WorkflowAgentSprite.css';
import {
  WORKFLOW_OPERATOR_AGENT_NAME,
  getWorkflowOperatorSpritePalette,
} from './workflowAgentSpriteColors';

/** Sprite animation modes (driven by GhostCursorOverlay). */
export const WORKFLOW_AGENT_SPRITE_MODES = [
  'idle',
  'waiting',
  'working',
  'waving',
  'acting',
  'entering',
  'exiting',
];

const SPRITE_MODES = new Set(WORKFLOW_AGENT_SPRITE_MODES);

/**
 * Codex-style pixel sprite for the workflow operator ghost cursor.
 * Drawn on a 32×32 grid — no rounded corners, crisp edges only.
 */
export default function WorkflowAgentSprite({
  mode = 'idle',
  size = 48,
  agentName = WORKFLOW_OPERATOR_AGENT_NAME,
}) {
  const spriteMode = SPRITE_MODES.has(mode) ? mode : 'idle';
  const visualMode =
    spriteMode === 'entering' || spriteMode === 'exiting' ? spriteMode : spriteMode;
  const paletteStyle = useMemo(
    () => getWorkflowOperatorSpritePalette(agentName),
    [agentName]
  );

  return (
    <svg
      className={`workflow-agent-sprite workflow-agent-sprite--${visualMode}`}
      style={paletteStyle}
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      shapeRendering="crispEdges"
      aria-hidden="true"
    >
      <defs>
        <g id="workflow-agent-pixel-outline">
          {/* Hard pixel shadow under feet */}
          <rect x="11" y="30" width="10" height="1" fill="var(--px-shadow)" opacity="0.55" />
        </g>
      </defs>

      <use href="#workflow-agent-pixel-outline" />

      <g className="workflow-agent-sprite__body">
        {/* Antenna */}
        <rect x="15" y="1" width="2" height="1" fill="var(--px-outline)" />
        <rect x="15" y="2" width="2" height="3" fill="var(--px-accent-mid)" />
        <rect x="14" y="2" width="1" height="1" fill="var(--px-highlight)" />
        <rect x="15" y="0" width="2" height="1" fill="var(--px-glow)" className="workflow-agent-sprite__antenna-tip" />

        {/* Head shell */}
        <rect x="10" y="5" width="12" height="9" fill="var(--px-outline)" />
        <rect x="11" y="6" width="10" height="7" fill="var(--px-accent-dark)" />
        <rect x="11" y="6" width="4" height="2" fill="var(--px-highlight)" />
        <rect x="17" y="11" width="4" height="2" fill="var(--px-accent-deep)" />

        {/* Face screen */}
        <g className="workflow-agent-sprite__screen">
          <rect x="12" y="8" width="8" height="4" fill="var(--px-screen)" />
          <rect x="12" y="8" width="8" height="1" fill="var(--px-screen-shine)" />
          <rect
            x="12"
            y="10"
            width="8"
            height="1"
            fill="var(--px-eye-shine)"
            className="workflow-agent-sprite__screen-scan"
          />
          <rect x="13" y="9" width="2" height="2" fill="var(--px-eye)" className="workflow-agent-sprite__eye workflow-agent-sprite__eye--left" />
          <rect x="17" y="9" width="2" height="2" fill="var(--px-eye)" className="workflow-agent-sprite__eye workflow-agent-sprite__eye--right" />
          <rect x="14" y="10" width="1" height="1" fill="var(--px-eye-shine)" />
          <rect x="18" y="10" width="1" height="1" fill="var(--px-eye-shine)" />
        </g>

        {/* Torso */}
        <rect x="9" y="14" width="14" height="10" fill="var(--px-outline)" />
        <rect x="10" y="15" width="12" height="8" fill="var(--px-accent-mid)" />
        <rect x="10" y="15" width="5" height="3" fill="var(--px-highlight)" />
        <rect x="17" y="20" width="5" height="3" fill="var(--px-accent-deep)" />
        {/* Chest badge */}
        <rect x="14" y="17" width="4" height="3" fill="var(--px-accent-dark)" />
        <rect x="15" y="18" width="2" height="1" fill="var(--px-glow)" />

        {/* Legs */}
        <rect x="11" y="24" width="4" height="5" fill="var(--px-outline)" />
        <rect x="17" y="24" width="4" height="5" fill="var(--px-outline)" />
        <rect x="12" y="25" width="2" height="3" fill="var(--px-accent-dark)" />
        <rect x="18" y="25" width="2" height="3" fill="var(--px-accent-dark)" />
        <rect x="11" y="28" width="4" height="1" fill="var(--px-accent-deep)" />
        <rect x="17" y="28" width="4" height="1" fill="var(--px-accent-deep)" />

        {/* Left arm (waves / types) */}
        <g className="workflow-agent-sprite__arm workflow-agent-sprite__arm--left">
          <rect x="5" y="16" width="4" height="7" fill="var(--px-outline)" />
          <rect x="6" y="17" width="2" height="5" fill="var(--px-accent-mid)" />
          <rect x="6" y="17" width="1" height="2" fill="var(--px-highlight)" />
          <rect x="4" y="22" width="5" height="3" fill="var(--px-outline)" />
          <rect x="5" y="23" width="3" height="2" fill="var(--px-screen)" />
        </g>

        {/* Right arm */}
        <g className="workflow-agent-sprite__arm workflow-agent-sprite__arm--right">
          <rect x="23" y="16" width="4" height="7" fill="var(--px-outline)" />
          <rect x="24" y="17" width="2" height="5" fill="var(--px-accent-mid)" />
          <rect x="25" y="17" width="1" height="2" fill="var(--px-highlight)" />
          <rect x="23" y="22" width="5" height="3" fill="var(--px-outline)" />
          <rect x="24" y="23" width="3" height="2" fill="var(--px-screen)" />
        </g>

        {/* Work sparkles (acting only) */}
        <rect className="workflow-agent-sprite__spark workflow-agent-sprite__spark--a" x="26" y="8" width="2" height="2" fill="var(--px-glow)" />
        <rect className="workflow-agent-sprite__spark workflow-agent-sprite__spark--b" x="28" y="12" width="1" height="1" fill="var(--px-spark)" />
        <rect className="workflow-agent-sprite__spark workflow-agent-sprite__spark--c" x="4" y="10" width="1" height="1" fill="var(--px-spark)" />
      </g>
    </svg>
  );
}
